import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Check, Loader2, Music, Clapperboard, Coins } from "lucide-react";
import { trackEvent } from '@/lib/analytics';
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/useAuth";
import { PackCard, type Pack } from "@/components/pricing/PackCard";
import { takeStashedToken } from "@/lib/signin-stash";
import { ApprovalRail } from "@/components/trust/ApprovalRail";
import { CheckoutStrip } from "@/components/trust/CheckoutStrip";
import { SystemChecks } from "@/components/trust/SystemChecks";
import { SupportLine } from "@/components/trust/SupportLine";

// Pricing — the per-video packs for the two engine products, and the credit
// ladder (30 -> 15,000). Everything comes from /api/web/packs, which serves
// shared/config/packs.js — the same objects Stripe checkout and BOTH webhooks
// validate against — so a price change is one config edit and this page can
// never drift from what's actually charged. The Telegram studio lists the
// same credit packs from the same file; credits are one balance everywhere.
//
// A corrido render costs 1000 credits, a story 25. Credits bought from the
// ladder spend on either, and on studio clips in Telegram.
//
// FROM THE TELEGRAM STUDIO (2026-09-13). A "Buy credits" tap in the studio
// opened this page as /pricing?from=studio&signin=<one-time token>[&pack=][&lang=]
// (services/studio-signin.js). New taps open the landing page's credits
// section instead (components/landing/CreditPacksSection.tsx, same rules);
// links already sent keep working here. The owner's rule is that this path has no
// difficulty in it, so on that visit the page:
//
//   * signs the customer in with the token -- no email, no password, no
//     sign-up -- and takes the token out of the address bar at once;
//   * shows the credit packs first and only (the per-video packs are the
//     website's own products), with the pack that covers what they were
//     making picked and scrolled into view;
//   * sends Checkout with return_to 'studio', so Stripe returns them to
//     /billing/success and its one "Back to the studio" button;
//   * on a cancelled checkout comes back here with the pack still picked and
//     one plain line (?checkout=cancelled, for website buyers too).
//
// A website visitor who is not signed in keeps the ordinary path, minus one
// step: the pack they tapped survives the sign-up and is picked on return.

type SigninState = "none" | "working" | "done" | "failed";

/** The token index.html took out of the address bar; removed as it is read, so it is used once. */
const takeStashedSignin = () => takeStashedToken("studio_signin");

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = localStorage.getItem("auth_token");
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

/**
 * A bot top-up link that still points here (a BILLING_URL set to /pricing)
 * goes to the landing page's credits section, the only page that reads the
 * bot's token (index.html kept it in sessionStorage 'bot_signin'). Left here,
 * the token was dropped unread and the purchase went to whatever account the
 * browser held.
 */
export default function Pricing() {
  const [params] = useSearchParams();
  if (params.get("from") === "bot") {
    return <Navigate replace to={{ pathname: "/", search: `?${params.toString()}`, hash: "#credits" }} />;
  }
  return <PricingPage />;
}

function PricingPage() {
  const { t, i18n } = useTranslation();
  const { refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [packs, setPacks] = useState<Pack[]>([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState<string | null>(null);
  const [error, setError] = useState("");

  // Read once: the address bar is rewritten below (the token comes out), and
  // the visit stays a studio visit whatever the router says afterwards. The
  // token is normally already out of the address by now -- index.html moves
  // it to sessionStorage before the analytics snippets run -- so it is taken
  // from there, and from the address only when that script could not run.
  const [entry] = useState(() => ({
    fromStudio: params.get("from") === "studio",
    signin: params.get("signin") || takeStashedSignin(),
    lang: params.get("lang"),
  }));
  const fromStudio = entry.fromStudio || params.get("from") === "studio";
  const wanted = params.get("pack");
  const cancelled = params.get("checkout") === "cancelled";
  const [signin, setSignin] = useState<SigninState>(entry.signin ? "working" : "none");
  const signinStarted = useRef(false);
  const loggedIn = !!api.getToken();

  useEffect(() => {
    fetch("/api/web/packs")
      .then((r) => r.json())
      .then((j) => setPacks(Array.isArray(j.data) ? j.data : []))
      .catch(() => setError(t("pricingPage.loadError")))
      .finally(() => setLoading(false));
  }, [t]);

  // The studio's language, so a Spanish customer is not handed English.
  useEffect(() => {
    const lang = entry.lang;
    if ((lang === "en" || lang === "es") && !String(i18n.language || "").startsWith(lang)) {
      void i18n.changeLanguage(lang);
    }
  }, [entry, i18n]);

  // SIGN IN WITH THE STUDIO'S ONE-TIME TOKEN. It leaves the address bar
  // before anything else happens: a used token is dead, and it should not sit
  // in the history, a bookmark or a screenshot either. The token replaces any
  // session this browser already had -- the credits must land on the account
  // the studio uses.
  useEffect(() => {
    if (!entry.signin || signinStarted.current) return;
    signinStarted.current = true;
    const next = new URLSearchParams(params);
    next.delete("signin");
    next.delete("lang");
    setParams(next, { replace: true });
    void (async () => {
      const r = await api.studioSignIn(entry.signin as string);
      if (r.success) {
        await refreshUser();
        setSignin("done");
        trackEvent("studio_signin_completed", { pack_type: wanted || null });
      } else {
        setSignin("failed");
        trackEvent("studio_signin_failed", { reason: r.error || null });
      }
    })();
    // Runs once per visit; `entry` is fixed at first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // STRAIGHT TO THE PACKS. The picked pack when there is one, else the credit
  // packs, whenever the visit is from the studio, carries a pick, or is a
  // cancelled checkout coming back.
  const scrolled = useRef(false);
  useEffect(() => {
    if (loading || scrolled.current) return;
    if (!(fromStudio || wanted || cancelled)) return;
    scrolled.current = true;
    const target = (wanted && document.getElementById(`pack-${wanted}`))
      || document.getElementById("credit-packs");
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: wanted ? "center" : "start" });
    }
  }, [loading, fromStudio, wanted, cancelled]);

  const buy = async (packType: string) => {
    if (signin === "working") return;
    // A FAILED STUDIO SIGN-IN BUYS NOTHING (2026-09-13 review). The exchange
    // leaves whatever session this browser already had -- another website
    // account, someone else's on a shared phone -- and Checkout would go
    // ahead on it with return_to 'studio': paid, "Back to the studio", and
    // the credits on an account the studio does not use. Only a session this
    // visit's exchange produced buys from the studio; the way back to one is
    // the studio's own button, which the failed line shows.
    if (signin === "failed") return;
    if (!api.getToken()) {
      // From the studio there is no sign-up to send anyone to: a website
      // account made here would not be the one the studio shows. The way
      // back to a working link is the studio's own button.
      if (fromStudio) {
        setSignin("failed");
        return;
      }
      const back = `/pricing?pack=${encodeURIComponent(packType)}`;
      window.location.href = `/signup?redirect_to=${encodeURIComponent(back)}`;
      return;
    }
    setBuying(packType);
    setError("");
    try {
      const r = await fetch("/api/web/checkout", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ pack_type: packType, ...(fromStudio ? { return_to: "studio" } : {}) }),
      });
      const j = await r.json();
      // From the studio, a refused session (expired, or an account that was
      // moved into the one linked in the studio) is the same dead end as a
      // failed sign-in: back to the studio for a fresh link.
      if (fromStudio && r.status === 401) {
        setSignin("failed");
        setBuying(null);
        return;
      }
      if (!r.ok || !j.url) throw new Error(j.details || j.error || t("pricingPage.checkoutFailed"));
      const selectedPack = packs.find((pack) => pack.type === packType);
      trackEvent('checkout_started', {
        checkout_flow: 'credit_pack',
        pack_type: packType,
        pack_kind: selectedPack?.kind,
        credit_amount: selectedPack?.points,
        currency: selectedPack?.currency,
        source: fromStudio ? 'studio' : 'web',
      });
      window.location.href = j.url; // Stripe-hosted checkout
    } catch (e: unknown) {
      setError(t("pricingPage.checkoutFailedDetail", { reason: e instanceof Error ? e.message : t("pricingPage.unknownError") }));
      setBuying(null);
    }
  };

  const corridoPacks = packs.filter((p) => p.type.startsWith("corrido"));
  const storyPacks = packs.filter((p) => p.type.startsWith("story"));
  const creditPacks = packs
    .filter((p) => p.kind === "credits" || p.type.startsWith("credit_"))
    .sort((a, b) => a.points - b.points);
  // Studio and agency sizes (2,000 cr and up) sit in their own row under the everyday packs.
  const STUDIO_SIZE = 2000;
  const everydayPacks = creditPacks.filter((p) => p.points < STUDIO_SIZE);
  const studioPacks = creditPacks.filter((p) => p.points >= STUDIO_SIZE);
  const videosIn = (p: Pack) => p.type.startsWith("corrido")
      ? t("pricingPage.videos", { count: Math.floor(p.points / 1000) })
      : t("pricingPage.stories", { count: Math.floor(p.points / 25) });
  const pickBadge = fromStudio ? t("pricingPage.pickedForYou") : t("pricingPage.yourPick");
  const holding = signin === "working";
  // Held while signing in, and for good once it failed (see buy()).
  const packsLocked = holding || signin === "failed";

  return (
    <div className="min-h-screen bg-background px-4 py-16 text-foreground">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight">
            {fromStudio ? t("pricingPage.studioTitle") : t("pricingPage.subtitle")}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            {fromStudio ? t("pricingPage.studioLead") : t("pricingPage.lead")}
          </p>
        </div>

        {/* One plain line at a time about where the visit stands. */}
        <div className="mx-auto mb-8 max-w-2xl space-y-3" aria-live="polite">
          {holding && (
            <div data-testid="studio-signing-in" className="flex items-center justify-center gap-2 rounded-lg border border-border bg-card p-4 text-sm">
              <Loader2 className="h-4 w-4 animate-spin text-primary" /> {t("pricingPage.signingIn")}
            </div>
          )}
          {fromStudio && signin !== "working" && signin !== "failed" && loggedIn && (
            <div data-testid="studio-signed-in" className="flex items-center justify-center gap-2 rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm">
              <Check className="h-4 w-4 text-primary" /> {t("pricingPage.signedInStudio")}
            </div>
          )}
          {signin === "failed" && (
            <div data-testid="studio-signin-failed" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-center text-sm">
              <p className="mb-3 text-foreground">{t("pricingPage.signinFailed")}</p>
              <a href="/go/studio" className="inline-block rounded-lg bg-primary px-5 py-2 font-bold text-primary-foreground hover:opacity-90">
                {t("pricingPage.backToStudio")}
              </a>
            </div>
          )}
          {cancelled && (
            <div data-testid="checkout-cancelled" className="rounded-lg border border-border bg-card p-4 text-center text-sm text-foreground">
              {t("pricingPage.cancelled")}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : (
          <>
            {/* The website's own per-video products. Not on a studio visit:
                the customer came for credits, and two other price lists
                above the one they came for is a place to get lost. */}
            {!fromStudio && (
              <>
                <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-primary">
                  <Music className="h-4 w-4" /> {t("pricingPage.corridoHeading")}
                </div>
                <p className="mb-6 max-w-2xl text-sm text-muted-foreground">{t("pricingPage.corridoBlurb")}</p>
                <div className="mb-12 grid gap-6 sm:grid-cols-2">
                  {corridoPacks.map((p) => (
                    <PackCard key={p.type} pack={p}
                      highlight={p.type === "corrido_1" || p.type === wanted}
                      badge={p.type === wanted ? pickBadge : p.type === "corrido_1" ? t("pricingPage.foundingPrice") : undefined}
                      subtitle={videosIn(p)} onBuy={buy} buying={buying} />
                  ))}
                </div>

                <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-primary">
                  <Clapperboard className="h-4 w-4" /> {t("pricingPage.storyHeading")}
                </div>
                <p className="mb-6 max-w-2xl text-sm text-muted-foreground">{t("pricingPage.storyBlurb")}</p>
                <div className="mb-12 grid gap-6 sm:grid-cols-2">
                  {storyPacks.map((p) => (
                    <PackCard key={p.type} pack={p} highlight={p.type === wanted}
                      badge={p.type === wanted ? pickBadge : undefined}
                      subtitle={videosIn(p)} onBuy={buy} buying={buying} />
                  ))}
                </div>
              </>
            )}

            {creditPacks.length > 0 && (
              <section id="credit-packs" data-testid="credit-packs" className="scroll-mt-24">
                <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-primary">
                  <Coins className="h-4 w-4" /> {t("pricingPage.creditsHeading")}
                </div>
                <p className="mb-6 max-w-2xl text-sm text-muted-foreground">{t("pricingPage.creditsBlurb")}</p>
                <div className="mb-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {everydayPacks.map((p) => (
                    <PackCard key={p.type} pack={p} highlight={p.type === wanted}
                      badge={p.type === wanted ? pickBadge : undefined}
                      subtitle={t("pricingPage.creditPack")} onBuy={buy} buying={buying} locked={packsLocked} />
                  ))}
                </div>
                {studioPacks.length > 0 && (
                  <div data-testid="studio-agency-packs">
                    <h3 className="mb-2 text-sm font-bold uppercase tracking-widest text-foreground/80">{t("pricingPage.studioSizesHeading")}</h3>
                    <p className="mb-6 max-w-2xl text-sm text-muted-foreground">{t("pricingPage.studioSizesBlurb")}</p>
                    <div className="mb-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      {studioPacks.map((p) => (
                        <PackCard key={p.type} pack={p} highlight={p.type === wanted}
                          badge={p.type === wanted ? pickBadge : undefined}
                          subtitle={t("pricingPage.creditPack")} onBuy={buy} buying={buying} locked={packsLocked} />
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}
          </>
        )}

        {error && <div className="mb-8 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">{error}</div>}

        {/* The trust pieces (Studio Blueprint 05, 2026-10-16): how people can
            pay before the Stripe page, where money moves, what the site
            promises and a person to ask. A studio visit buys Telegram studio
            credits, so it skips the website's approve-then-render rail. */}
        <div className="mb-12 space-y-8" data-testid="pricing-trust">
          <CheckoutStrip methods="packs" className="mt-0" />
          {!fromStudio && <ApprovalRail kind="studio" />}
          <SystemChecks checks={fromStudio ? ["failedRefunds", "cardsToStripe"] : ["noChargeBeforeApprove", "failedRefunds", "cardsToStripe"]} />
          <SupportLine where="pricing" />
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
          <div className="mb-2 flex items-center gap-2 font-semibold text-foreground"><Check className="h-4 w-4 text-primary" /> {t("pricingPage.howItWorks")}</div>
          <ul className="list-inside list-disc space-y-1">
            {/* A studio visit gets only the rules true of studio credits. */}
            {!fromStudio && <li>{t("pricingPage.rule1")}</li>}
            {!fromStudio && <li>{t("pricingPage.rule2")}</li>}
            <li>{t("pricingPage.rule6")}</li>
            {!fromStudio && <li>{t("pricingPage.rule3")}</li>}
            <li>{t("pricingPage.rule4")}</li>
            <li>{t("pricingPage.rule5")}</li>
          </ul>
          <p className="mt-4">{t("pricingPage.stripeNote")} <Link to="/#faq" className="text-primary underline">{t("pricingPage.seeFaq")}</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
